import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlagsContext } from "@/hooks/useFeatureFlag";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { ADMIN_NAV, ALWAYS_ON_KEYS } from "@/constants/featureRegistry";

export default function AdminLayout({ children }) {
  const { user, loading: authLoading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { features, loading: ffLoading, error: ffError } = useFeatureFlagsContext();
  const { subscription, loading: subLoading } = useSubscription();

  // Gating FAIL-CLOSED: menu ter-gate hanya tampil kalau feature ENABLED.
  // Fail-open HANYA saat fetch feature flags gagal total (network outage).
  const isVisible = (item) => {
    if (!item.featureKey) return true;
    if (ALWAYS_ON_KEYS.includes(item.featureKey)) return true;
    if (ffError) return true;
    const f = features[item.featureKey];
    return !!(f && f.enabled);
  };
  const visibleNav = ADMIN_NAV.filter(isVisible);

  // Gate 1: while AUTH/SUBSCRIPTION/FEATURE-FLAGS resolve, do not render children
  // (children would fetch data with wrong scope, dan menu ter-gate akan flash).
  if (authLoading || (user && ["admin", "owner"].includes(user.role) && (subLoading || ffLoading))) {
    return (
      <div
        className="min-h-screen grid place-items-center p-4"
        style={{ backgroundColor: "var(--brand-background)" }}
        data-testid="admin-subscription-loading"
      >
        <div className="text-sm text-[#5C5C5C] font-semibold animate-pulse">Memuat dashboard…</div>
      </div>
    );
  }

  if (!user || !["admin", "owner"].includes(user.role)) {
    return (
      <div className="min-h-screen grid place-items-center p-4" style={{ backgroundColor: "var(--brand-background)" }}>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E5E5] text-center max-w-md">
          <h2 className="font-display text-2xl font-bold mb-2">Akses Ditolak</h2>
          <p className="text-[#5C5C5C] mb-4">Anda harus login sebagai admin untuk mengakses halaman ini.</p>
          <Link to="/login"><Button className="brand-btn-primary rounded-xl">Login Admin</Button></Link>
        </div>
      </div>
    );
  }

  // Subscription lock: block ALL admin content (no children rendered → no fetch of admin/public data)
  // when tenant subscription is SUSPENDED or EXPIRED. Only SubscriptionBanner + lock notice shown.
  // Fail-open when no subscription record exists (new tenant without a plan yet).
  // Note: subLoading is already gated at the top of the component (before role check).
  const subStatus = subscription?.status;
  const isLocked = subStatus === "SUSPENDED" || subStatus === "EXPIRED";
  if (isLocked) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--brand-background)" }} data-testid="admin-subscription-locked">
        <header className="bg-white border-b border-[#E5E5E5] px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo variant="admin" size="sm" to="/admin" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
              <div className="text-sm font-semibold truncate max-w-[200px]">{user.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-locked-logout-btn"
            >
              <LogOut size={14} /> Keluar
            </Button>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            <SubscriptionBanner />
            <div
              className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5] shadow-sm text-center"
              data-testid="admin-subscription-lock-notice"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">
                {subStatus === "EXPIRED" ? "Langganan Berakhir" : "Langganan Ditangguhkan"}
              </h2>
              <p className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed">
                {subStatus === "EXPIRED"
                  ? "Masa aktif langganan tenant Anda telah berakhir. Semua fitur admin dinonaktifkan sementara untuk melindungi data toko. Silakan perbarui langganan atau hubungi Platform Owner."
                  : "Langganan tenant Anda sedang ditangguhkan oleh Platform Owner. Semua fitur admin dinonaktifkan sementara. Silakan hubungi Platform Owner untuk mengaktifkan kembali."}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--brand-background)" }}>
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#E5E5E5] sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-[#E5E5E5]">
          <Logo variant="admin" size="md" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  active
                    ? "brand-active-menu"
                    : "text-[#5C5C5C] hover:bg-[#FAF9F6] hover:text-[#1A1A1A]"
                }`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E5E5E5]">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
            <div className="text-sm font-semibold truncate">{user.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#5C5C5C] hover:bg-[#FAF9F6]"
          >
            <LogOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo variant="admin" size="sm" to="/admin" />
          <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/"); }}>
            <LogOut size={14} /> Keluar
          </Button>
        </header>

        {/* mobile horizontal nav */}
        <div className="md:hidden bg-white border-b border-[#E5E5E5] overflow-x-auto no-scrollbar">
          <div className="flex gap-1 p-2 min-w-max">
            {visibleNav.map((item) => {
              const active = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                    active ? "brand-active-menu" : "text-[#5C5C5C]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <SubscriptionBanner />
          {children}
        </div>
      </div>
    </div>
  );
}
